interface AuthTokens {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface UserInfo {
  email: string;
  name: string;
  username: string;
}

const COGNITO_DOMAIN = 'https://showandtell-620396488885-us-east-2.auth.us-east-2.amazoncognito.com';
const CLIENT_ID = '2drnivnv1a3ekccmv26bs9jl8k';
const REDIRECT_URI = window.location.origin;

// API base URL for auth proxy
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://cy7v0la0lk.execute-api.us-east-2.amazonaws.com/prod'
  : 'http://localhost:8000';

class AuthService {
  private tokens: AuthTokens | null = null;
  private inactivityTimer: NodeJS.Timeout | null = null;
  private readonly INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds

  constructor() {
    this.loadTokensFromStorage();
    this.setupInactivityTimer();
    this.setupBeforeUnloadHandler();
    this.setupStorageListener();
  }

  private loadTokensFromStorage(): void {
    const stored = localStorage.getItem('auth_tokens');
    if (stored) {
      this.tokens = JSON.parse(stored);
    }
  }

  private saveTokensToStorage(tokens: AuthTokens): void {
    this.tokens = tokens;
    localStorage.setItem('auth_tokens', JSON.stringify(tokens));
    this.resetInactivityTimer();
  }

  private clearTokensFromStorage(): void {
    this.tokens = null;
    localStorage.removeItem('auth_tokens');
    localStorage.removeItem('selectedTeamId');
    this.clearInactivityTimer();
  }

  private setupInactivityTimer(): void {
    // Reset timer on user activity
    const resetTimer = () => this.resetInactivityTimer();
    
    // Listen for user activity events
    document.addEventListener('mousedown', resetTimer);
    document.addEventListener('mousemove', resetTimer);
    document.addEventListener('keypress', resetTimer);
    document.addEventListener('scroll', resetTimer);
    document.addEventListener('touchstart', resetTimer);
    
    this.resetInactivityTimer();
  }

  private resetInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
    }
    
    // Only set timer if user is authenticated
    if (this.tokens) {
      this.inactivityTimer = setTimeout(() => {
        console.log('Auto-logout due to inactivity');
        this.logout();
        // Optionally show a message to user
        alert('You have been logged out due to inactivity.');
        window.location.reload();
      }, this.INACTIVITY_TIMEOUT);
    }
  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  private setupBeforeUnloadHandler(): void {
    // Note: beforeunload fires for both tab close and browser close
    // We can't reliably distinguish between them, so we'll remove this
    // and rely on inactivity timeout and manual logout instead
    
    // If you want to clear tokens on browser close, you could use:
    // window.addEventListener('beforeunload', () => {
    //   this.clearTokensFromStorage();
    // });
    // But this will also clear on tab close, which we don't want
  }

  private setupStorageListener(): void {
    // Listen for localStorage changes from other tabs
    window.addEventListener('storage', (event) => {
      // Only care about auth_tokens changes
      if (event.key === 'auth_tokens') {
        if (event.newValue === null) {
          // Tokens were removed in another tab (logout)
          this.tokens = null;
          this.clearInactivityTimer();
          // Redirect to login
          window.location.href = `${COGNITO_DOMAIN}/login?client_id=${CLIENT_ID}&response_type=code&scope=email+openid+profile&redirect_uri=${encodeURIComponent(REDIRECT_URI)}`;
        }
      }
    });
  }

  isAuthenticated(): boolean {
    // We're authenticated if we have tokens (even if access token is expired)
    // The API interceptor will handle refreshing expired access tokens
    return this.tokens !== null;
  }

  getAccessToken(): string | null {
    return this.isAuthenticated() ? this.tokens!.accessToken : null;
  }

  async refreshTokens(): Promise<void> {
    if (!this.tokens?.refreshToken) {
      throw new Error('No refresh token available');
    }

    const endpoint = process.env.NODE_ENV === 'production' 
      ? `${API_BASE_URL}/auth/refresh-token`
      : `${API_BASE_URL}/api/auth/refresh-token`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        refreshToken: this.tokens.refreshToken
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Token refresh failed: ${response.status} - ${errorText}`);
    }

    const data = await response.json();
    
    const newTokens: AuthTokens = {
      accessToken: data.accessToken,
      idToken: data.idToken,
      refreshToken: data.refreshToken,
      expiresAt: Date.now() + (10 * 60 * 1000) // 10 minutes from now
    };
    
    this.saveTokensToStorage(newTokens);
  }

  redirectToLogin(): void {
    const params = new URLSearchParams({
      client_id: CLIENT_ID,
      response_type: 'code',
      scope: 'email openid profile',
      redirect_uri: REDIRECT_URI
    });
    
    window.location.href = `${COGNITO_DOMAIN}/login?${params.toString()}`;
  }

  async handleCallback(): Promise<boolean> {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (!code) return false;

    try {
      const tokens = await this.exchangeCodeForTokens(code);
      this.saveTokensToStorage(tokens);
      
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
      
      return true;
    } catch (error) {
      console.error('Token exchange failed:', error);
      return false;
    }
  }

  private async exchangeCodeForTokens(code: string): Promise<AuthTokens> {
    const endpoint = process.env.NODE_ENV === 'production' 
      ? `${API_BASE_URL}/auth/exchange-code`
      : `${API_BASE_URL}/api/auth/exchange-code`;

    const response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        code: code,
        redirectUri: REDIRECT_URI
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token exchange error:', response.status, errorText);
      throw new Error(`Token exchange failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();
    
    return {
      accessToken: data.accessToken,
      idToken: data.idToken,
      refreshToken: data.refreshToken,
      expiresAt: Date.now() + (10 * 60 * 1000) // 10 minutes from now
    };
  }

  getUserInfo(): UserInfo | null {
    if (!this.tokens?.idToken) return null;
    
    try {
      const payload = JSON.parse(atob(this.tokens.idToken.split('.')[1]));
      const name = payload.name || `${payload.given_name || ''} ${payload.family_name || ''}`.trim() || '';
      
      // Extract alias from email if preferred_username not available
      let alias = payload.preferred_username || '';
      if (!alias && payload.email) {
        alias = payload.email.split('@')[0]; // Extract part before @
      }
      
      // Remove AmazonFederate_ prefix if present
      alias = alias.replace(/^AmazonFederate_/, '');
      
      return {
        email: payload.email || '',
        name: name,
        username: alias
      };
    } catch (error) {
      console.error('Failed to parse ID token:', error);
      return null;
    }
  }

  logout(): void {
    this.clearTokensFromStorage();
    window.location.href = `${COGNITO_DOMAIN}/logout?client_id=${CLIENT_ID}&logout_uri=${encodeURIComponent(window.location.origin)}`;
  }
}

export const authService = new AuthService();
